/**
 * Lead Flow Schema v2
 * Stored in LeadFlow.schema (draft) and LeadFlow.publishedSchema (live).
 * Compiled to SurveyJS JSON at render time.
 */

import type { SkinScanMetricKey } from './skin-scan-types.js';

/** Public storefront payload from GET /lead-flows/slug/:slug */
export type PublishedLeadFlow = {
  id: string;
  name: string;
  slug: string;
  schema: unknown;
  version: number;
};

export type FlowStepType =
  | 'radiogroup'
  | 'checkbox'
  | 'text'
  | 'rating'
  | 'imagepicker'
  | 'html'
  | 'skin-scan'
  | 'lead-gate'
  | 'end';

export interface FlowChoice {
  value: string;
  label: string;
  imageUrl?: string;
}

export interface FlowBranchRule {
  skipTo: string;
}

interface FlowStepBase {
  id: string;
  type: FlowStepType;
  title: string;
  description?: string;
  required?: boolean;
  orderWeight?: number;
}

export interface FlowStepRadiogroup extends FlowStepBase {
  type: 'radiogroup';
  choices: FlowChoice[];
  branching?: Record<string, FlowBranchRule>;
}

export interface FlowStepCheckbox extends FlowStepBase {
  type: 'checkbox';
  choices: FlowChoice[];
}

export interface FlowStepText extends FlowStepBase {
  type: 'text';
  placeholder?: string;
  inputType?: 'text' | 'email' | 'phone' | 'number' | 'textarea';
}

export interface FlowStepRating extends FlowStepBase {
  type: 'rating';
  rateMin?: number;
  rateMax?: number;
  minRateDescription?: string;
  maxRateDescription?: string;
}

export interface FlowStepImagePicker extends FlowStepBase {
  type: 'imagepicker';
  choices: FlowChoice[];
  multiSelect?: boolean;
}

export interface FlowStepHtml extends FlowStepBase {
  type: 'html';
  htmlContent: string;
}

export interface FlowStepSkinScan extends FlowStepBase {
  type: 'skin-scan';
  mode?: 'mock' | 'live';
  captureDurationMs?: number;
  showMetrics?: SkinScanMetricKey[];
  disclaimerHtml?: string;
}

export interface FlowStepLeadGate extends FlowStepBase {
  type: 'lead-gate';
  fields: ('email' | 'phone' | 'firstName' | 'lastName' | 'name' | 'company')[];
  emailRequired?: boolean;
  phoneRequired?: boolean;
  consentText?: string;
  enrichment?: {
    enabled: boolean;
    skipFieldsIfKnown?: string[];
  };
}

export interface FlowStepEnd extends FlowStepBase {
  type: 'end';
  actionType: 'routine-match' | 'redirect' | 'thank-you';
  redirectUrl?: string;
  thankYouMessage?: string;
}

export type FlowStep =
  | FlowStepRadiogroup
  | FlowStepCheckbox
  | FlowStepText
  | FlowStepRating
  | FlowStepImagePicker
  | FlowStepHtml
  | FlowStepSkinScan
  | FlowStepLeadGate
  | FlowStepEnd;

export interface FlowSchemaMeta {
  title: string;
  description?: string;
  actionType?: string;
  thankYouMessage?: string;
}

export interface FlowSchema {
  version: 2;
  meta: FlowSchemaMeta;
  steps: FlowStep[];
}

export const STEP_TYPE_WEIGHTS: Record<FlowStepType, number> = {
  html: 10,
  radiogroup: 20,
  checkbox: 20,
  rating: 30,
  imagepicker: 30,
  text: 40,
  'skin-scan': 15,
  'lead-gate': 80,
  end: 100,
};

export const STEP_TYPE_LABELS: Record<FlowStepType, string> = {
  radiogroup: 'Choix unique',
  checkbox: 'Choix multiple',
  text: 'Texte libre',
  rating: 'Notation',
  imagepicker: "Sélecteur d'images",
  html: 'Contenu / Info',
  'skin-scan': 'Analyse peau (caméra)',
  'lead-gate': 'Capture de contact',
  end: 'Fin du flow',
};

export const STEP_TYPE_ICONS: Record<FlowStepType, string> = {
  radiogroup: '○',
  checkbox: '☑',
  text: '✎',
  rating: '★',
  imagepicker: '🖼',
  html: 'ℹ',
  'skin-scan': '📷',
  'lead-gate': '🔒',
  end: '✓',
};

export function createDefaultStep(type: FlowStepType): FlowStep {
  const base = {
    id: `step-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    title: '',
    orderWeight: STEP_TYPE_WEIGHTS[type],
  };

  switch (type) {
    case 'radiogroup':
      return {
        ...base,
        type,
        required: true,
        choices: [
          { value: 'option-1', label: 'Option 1' },
          { value: 'option-2', label: 'Option 2' },
          { value: 'option-3', label: 'Option 3' },
        ],
      };
    case 'checkbox':
      return {
        ...base,
        type,
        choices: [
          { value: 'option-1', label: 'Option 1' },
          { value: 'option-2', label: 'Option 2' },
        ],
      };
    case 'text':
      return { ...base, type, inputType: 'text', placeholder: '' };
    case 'rating':
      return { ...base, type, rateMin: 1, rateMax: 5 };
    case 'imagepicker':
      return { ...base, type, choices: [], multiSelect: false };
    case 'html':
      return { ...base, type, htmlContent: '<p>Contenu informatif</p>' };
    case 'skin-scan':
      return {
        ...base,
        type,
        title: 'Analyse de votre peau',
        required: true,
        mode: 'mock',
        captureDurationMs: 3500,
        showMetrics: ['hydration', 'texture', 'spots'],
        disclaimerHtml:
          '<p>Cet outil fournit des indications cosmétiques uniquement, sans valeur médicale. Les images ne sont pas enregistrées.</p>',
      };
    case 'lead-gate':
      return {
        ...base,
        type,
        title: 'Vos coordonnées',
        fields: ['email', 'phone'],
        emailRequired: true,
        consentText: "J'accepte d'être contacté(e)",
      };
    case 'end':
      return {
        ...base,
        type,
        title: 'Merci !',
        actionType: 'thank-you',
        thankYouMessage: 'Nous vous contacterons bientôt.',
      };
  }
}

export function createEmptyFlowSchema(): FlowSchema {
  return {
    version: 2,
    meta: { title: 'Nouveau Flow' },
    steps: [createDefaultStep('lead-gate'), createDefaultStep('end')],
  };
}

/** Commercial partner PDF-gate attribution flow (capture UI lives on commercial landing). */
export function createPartnerPortfolioFlowSchema(): FlowSchema {
  return {
    version: 2,
    meta: {
      title: 'Partner portfolio',
      description: 'PDF gate on commercial landing — name, company, email, phone',
    },
    steps: [
      {
        id: 'lead',
        type: 'lead-gate',
        title: 'Partnership contact',
        fields: ['name', 'email', 'phone'],
        emailRequired: true,
        consentText: 'I agree to be contacted about partnership opportunities.',
      },
      {
        id: 'end',
        type: 'end',
        title: 'Thank you',
        actionType: 'thank-you',
        thankYouMessage: 'Your portfolio is ready.',
      },
    ],
  };
}

/** Commercial landing contact form (message + identity; capture UI on commercial landing). */
export function createLandingContactFlowSchema(): FlowSchema {
  return {
    version: 2,
    meta: {
      title: 'Landing contact',
      description: 'Commercial contact section — name, company, email, phone, message',
    },
    steps: [
      {
        id: 'lead',
        type: 'lead-gate',
        title: 'Contact',
        fields: ['name', 'email', 'phone'],
        emailRequired: true,
        consentText: 'I agree to be contacted about partnership opportunities.',
      },
      {
        id: 'message',
        type: 'text',
        title: 'Message',
        required: true,
        inputType: 'textarea',
        placeholder: 'Brand, timeline, regulatory status…',
      },
      {
        id: 'end',
        type: 'end',
        title: 'Thank you',
        actionType: 'thank-you',
        thankYouMessage: 'Thanks — we will reply with a clear next step.',
      },
    ],
  };
}

/** Default regimen builder flow (mirrors storefront fr.data regimen wizard). */
export function createRegimenBuilderFlowSchema(): FlowSchema {
  return {
    version: 2,
    meta: {
      title: 'Créez votre routine',
      description: 'Répondez à quelques questions et recevez une routine personnalisée matin & soir.',
    },
    steps: [
      {
        id: 'skinType',
        type: 'radiogroup',
        title: 'Quel est votre type de peau ?',
        required: true,
        orderWeight: 20,
        choices: [
          { value: 'seche', label: 'Sèche' },
          { value: 'grasse', label: 'Grasse' },
          { value: 'mixte', label: 'Mixte' },
          { value: 'sensible', label: 'Sensible' },
          { value: 'normale', label: 'Normale' },
        ],
      },
      {
        id: 'concern',
        type: 'radiogroup',
        title: 'Quel est votre objectif principal ?',
        required: true,
        orderWeight: 20,
        choices: [
          { value: 'hydratation', label: 'Hydratation' },
          { value: 'antiage', label: 'Anti-âge' },
          { value: 'imperfections', label: 'Anti-imperfections' },
          { value: 'teint', label: 'Éclat du teint' },
          { value: 'protection', label: 'Protection' },
        ],
      },
      {
        id: 'texture',
        type: 'radiogroup',
        title: 'Quelle texture préférez-vous ?',
        required: true,
        orderWeight: 20,
        choices: [
          { value: 'legeres', label: 'Textures légères' },
          { value: 'confort', label: 'Textures confort' },
          { value: 'sans-parfum', label: 'Sans parfum' },
        ],
      },
      {
        id: 'pace',
        type: 'radiogroup',
        title: 'Combien de temps pour votre routine ?',
        required: true,
        orderWeight: 20,
        choices: [
          { value: 'rapide', label: 'Express' },
          { value: 'complete', label: 'Complète' },
        ],
      },
      {
        id: 'lead-gate',
        type: 'lead-gate',
        title: 'Recevez votre routine personnalisée',
        orderWeight: 80,
        fields: ['email', 'phone'],
        emailRequired: false,
        phoneRequired: false,
        consentText: "J'accepte d'être contacté(e) par Wapharma",
      },
      {
        id: 'end',
        type: 'end',
        title: 'Merci !',
        orderWeight: 100,
        actionType: 'routine-match',
        thankYouMessage: 'Votre routine personnalisée est prête.',
      },
    ],
  };
}

/** Skin analyzer flow: camera scan (mock) → contact → routine match. */
export function createSkinAnalyzerFlowSchema(): FlowSchema {
  return {
    version: 2,
    meta: {
      title: 'Analyse de peau IA',
      description:
        'Scannez votre visage à la caméra pour une première analyse cosmétique et une routine adaptée.',
    },
    steps: [
      {
        id: 'skinScan',
        type: 'skin-scan',
        title: 'Scannez votre visage',
        description:
          'Placez votre visage dans le cadre. L’analyse est indicative et ne remplace pas un avis médical.',
        required: true,
        orderWeight: 15,
        mode: 'live',
        captureDurationMs: 3500,
        showMetrics: ['hydration', 'texture', 'spots', 'redness'],
        disclaimerHtml:
          '<p>En activant la caméra, vous acceptez un traitement local de l’image dans votre navigateur (aucun envoi serveur en phase prototype). Indications cosmétiques uniquement.</p>',
      },
      {
        id: 'lead-gate',
        type: 'lead-gate',
        title: 'Recevez votre analyse détaillée',
        orderWeight: 80,
        fields: ['email', 'phone'],
        emailRequired: false,
        phoneRequired: false,
        consentText: "J'accepte d'être contacté(e) par Hamssah pour des conseils personnalisés",
      },
      {
        id: 'end',
        type: 'end',
        title: 'Merci !',
        orderWeight: 100,
        actionType: 'routine-match',
        thankYouMessage: 'Votre routine personnalisée est prête.',
      },
    ],
  };
}
